/**
 * Connection model — joins the three Squad MCP surfaces (catalog ⋈ workspace
 * servers ⋈ per-agent links) into a per-integration view, and derives which
 * apps an agent is actually connected to.
 *
 * The crux of the UX: a scope group (Core / CRM / HRM) is only meaningful when
 * the agent has that app's MCP server attached AND enabled. Both the catalog
 * entry's `target_app_type` and a server's `catalog_target_app_type` carry the
 * same key Core's scope catalog groups by (`app`): `""` → `core`, else the
 * value (`crm`/`hrm`). {@link connectedApps} turns the agent's links into that
 * set so the scope picker can gate disconnected apps.
 */

import type {
  SquadAgentMcpLink,
  SquadMcpCatalogEntry,
  SquadMcpServer
} from 'src/services/mcpServers'

/** `""` (Core) → `"core"`; otherwise the cross-app key (`crm`/`hrm`). */
export const appForTargetType = (targetType: string): string =>
  targetType || 'core'

export const appForEntry = (entry: SquadMcpCatalogEntry): string =>
  appForTargetType(entry.target_app_type)

export const appForServer = (server: SquadMcpServer | undefined): string =>
  appForTargetType(server?.catalog_target_app_type ?? '')

/** A per-agent link resolved against its workspace server (for app + enabled). */
export type ResolvedLink = {
  link: SquadAgentMcpLink
  server?: SquadMcpServer
}

/** One catalog integration with this agent's connection state folded in. */
export type CatalogConnection = {
  entry: SquadMcpCatalogEntry
  /** `core` / `crm` / `hrm` — the scope-catalog app this entry maps to. */
  app: string
  /** Cross-app entries (CRM/HRM) need an instance token on connect + can detach. */
  isCrossApp: boolean
  /** Agent links whose server was provisioned from this catalog entry. */
  links: ResolvedLink[]
  /** The agent has at least one link for this entry. */
  attached: boolean
  /** Attached AND both the link and its server are enabled (callable). */
  enabled: boolean
}

/**
 * Build one {@link CatalogConnection} per catalog entry, attaching the agent's
 * links (and resolved servers) for that entry. Servers are matched to catalog
 * entries by `catalog_slug` (one server per entry — each app is
 * single-instance per workspace).
 */
export const buildConnections = (
  catalog: SquadMcpCatalogEntry[],
  servers: SquadMcpServer[],
  links: SquadAgentMcpLink[]
): CatalogConnection[] => {
  const serverById = new Map(servers.map((s) => [s.id, s]))
  const serversBySlug = new Map<string, SquadMcpServer[]>()
  for (const s of servers) {
    if (!s.catalog_slug) continue
    const arr = serversBySlug.get(s.catalog_slug) ?? []
    arr.push(s)
    serversBySlug.set(s.catalog_slug, arr)
  }

  return catalog.map((entry) => {
    const entryServerIds = new Set(
      (serversBySlug.get(entry.slug) ?? []).map((s) => s.id)
    )
    const entryLinks: ResolvedLink[] = links
      .filter((l) => entryServerIds.has(l.server))
      .map((l) => ({ link: l, server: serverById.get(l.server) }))

    return {
      entry,
      app: appForEntry(entry),
      isCrossApp: Boolean(entry.target_app_type),
      links: entryLinks,
      attached: entryLinks.length > 0,
      enabled: entryLinks.some(
        (el) => el.link.is_enabled && (el.server?.is_enabled ?? true)
      )
    }
  })
}

/**
 * The set of apps (`core`/`crm`/`hrm`) the agent can actually invoke tools for:
 * every enabled link whose (enabled) server maps to that app. Catalog-agnostic —
 * a hand-made Core server counts too. This gates the scope picker.
 */
export const connectedApps = (
  links: SquadAgentMcpLink[],
  servers: SquadMcpServer[]
): Set<string> => {
  const serverById = new Map(servers.map((s) => [s.id, s]))
  const apps = new Set<string>()
  for (const link of links) {
    if (!link.is_enabled) continue
    const server = serverById.get(link.server)
    if (server && !server.is_enabled) continue
    apps.add(appForServer(server))
  }
  return apps
}
